/* eslint-disable camelcase */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React from "react";
import Alert from "@material-ui/lab/Alert";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { Autocomplete, AutocompleteRenderInputParams } from "@material-ui/lab";
import { Grid, TextField } from "@material-ui/core";
import moment from "moment";
import { Button } from "components/haven-design/buttons";
import { Input } from "components/haven-design/form/inputs";
import { Modal } from "components/haven-design/modal";
import StructuredReasons from "components/structured-reasons";
import { InterestStage, STATUSES_REASON_LABEL } from "model/interest/status";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import styles from "./actions.module.css";
import { dateToString, skipReasons, unitSizes } from "./helpers";


interface Props {
  onRequestClose: () => void;
  onSubmit: () => void;
  onChange: (value: string) => void;
  onCancel: () => void;
  isOpen: boolean;
  isSubmitting: boolean;
  newStatus: Action | BulkAction | null;
  currentStatus: Action | BulkAction;
  reason: string;
  bulkAction?: boolean;
}

export enum Action {
  Add_Document = "Add Document",
  Add_Note = "Add Note",
  Change_Status = "Change Status",
  Log_Call = "Log Call",
  Moved_In = "Moved In",
  On_Waitlist = "On Waitlist",
  Removed = "Removed",
  Reset_Password = "Reset Password",
  Request_Forms = "Request Forms",
  Send_Full_App = "Send Full Application",
  Send_Message = "Send Message",
  Skip = "Skip",
  Sync_with_OneSite = "Sync with OneSite"
}

export enum BulkAction {
  Star = "Star",
  Unstar = "Unstar",
  Moved_In = "Moved In",
  On_Waitlist = "On Waitlist",
  Removed = "Removed",
  Skip = "Skip",
}

const statusIsActive = (status: Action | BulkAction) =>
  status !== Action.Moved_In && status !== Action.Removed;

const displayConfirmationTitle = (
  currentStatus: Action | BulkAction,
  newStatus: Action | BulkAction,
  bulkAction?: boolean,
) => {
  const reinstateMsg = "Why are you reinstating this applicant?";

  if (!statusIsActive(currentStatus) && statusIsActive(newStatus)) {
    return reinstateMsg;
  }
  switch (newStatus) {
    case Action.Removed:
      return STATUSES_REASON_LABEL[InterestStage.Removed];
    case Action.Moved_In:
      return STATUSES_REASON_LABEL[InterestStage.MovedIn];
    case Action.On_Waitlist:
      return reinstateMsg;
    case Action.Skip:
      if (bulkAction) {
        return "Why are you skipping these applicants?";
      }
      return "Why are you skipping this applicant?";

    default:
      return "Status Change";
  }
};

const displayPlaceholder = (status: Action | BulkAction) => {
  switch (status) {
    case Action.On_Waitlist:
    case Action.Removed:
    case Action.Skip:
      return "Enter a reason...";
    case Action.Moved_In:
      return "Unit number, move in date, etc...";
    default:
      return "";
  }
};

const ModalTitleText = ({ status }: { status: Action | BulkAction }) => (
  <>
    Changing the status to <strong>{status}</strong>&nbsp;
    requires a reason for auditing purposes.
  </>
);

const ReasonModal: React.FC<Props> = ({
  onRequestClose,
  onSubmit,
  onChange,
  onCancel,
  isOpen,
  currentStatus,
  newStatus,
  reason,
  isSubmitting,
  bulkAction,
}) => {
  const [isValid, setIsValid] = React.useState(reason !== "");
  const [actionDate, setActionDate] = React.useState<Date | null>(new Date());
  const [optionalDate, setOptionalDate] = React.useState<Date | null>(null);
  const [isSkipValid, setIsSkipValid] = React.useState(false);
  const [autocompleteValue, setAutocompleteValue] =
    React.useState<typeof skipReasons[number] | null>(null);
  const [optionalValue, setOptionalValue] = React.useState("");
  const emptyInputs = () => {
    setActionDate(new Date());
    setOptionalDate(null);
    onChange("");
    setIsSkipValid(false);
    setAutocompleteValue(null);
    setOptionalValue("");
  };
  const onSubmitWrapper = () => {
    onSubmit();
    emptyInputs();
  };
  const onCloseWrapper = () => {
    onRequestClose();
    emptyInputs();
  };
  React.useEffect(() => {
    if (!autocompleteValue) {
      onChange("");
      setIsSkipValid(false);
      return;
    }
    const actionDateString = moment(actionDate).format("MM/DD/YYYY");
    let optionalValueText = optionalValue;
    if (autocompleteValue.date) optionalValueText = dateToString(optionalDate);
    optionalValueText = optionalValueText ? `, ${optionalValueText}` : "";
    onChange(autocompleteValue.value ? `${actionDateString} - ${autocompleteValue.value}${optionalValueText}` : "");
    let skipValid = true;
    if (!autocompleteValue.value || Object.keys(autocompleteValue.value).length === 0) {
      skipValid = false;
    }
    if (autocompleteValue.required && !optionalValueText) skipValid = false;
    if (!actionDate) skipValid = false;

    setIsSkipValid(skipValid);
  }, [autocompleteValue, optionalValue, optionalDate, actionDate]);

  const handleRemovalReasonOnChange = (value: string) => {
    setIsValid(value !== "");
    onChange(value);
  };

  return (
    <Modal
      title={newStatus && displayConfirmationTitle(currentStatus, newStatus, bulkAction)}
      isOpen={isOpen}
      onRequestClose={onCloseWrapper}
      fullWidth
    >
      <DialogContent>
        {newStatus === Action.Skip && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <KeyboardDatePicker
                style={{ minWidth: "15em" }}
                required
                disableFuture
                autoComplete="off"
                autoOk
                variant="inline"
                inputVariant="outlined"
                label="Skip Date"
                format="MM/dd/yyyy"
                InputAdornmentProps={{ position: "end" }}
                value={actionDate}
                onChange={setActionDate}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <Autocomplete
                id="skip-reason-input"
                options={skipReasons}
                getOptionLabel={({ value }: {value: string}) => value}
                onChange={(_event, data: any) => {
                  setAutocompleteValue(data);
                  setOptionalValue(data?.date ? dateToString(new Date()) : "");
                }}
                renderInput={(params: AutocompleteRenderInputParams) => <TextField
                  required
                  label="Skip Reason"
                  variant="outlined" {...params} />}
              />
            </Grid>
            {autocompleteValue?.text && (<Grid item xs={12}>
              <TextField
                fullWidth
                label={autocompleteValue.text}
                placeholder={autocompleteValue.text}
                variant="outlined"
                required={autocompleteValue.required}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setOptionalValue(e.target.value);
                }}/>
            </Grid>)}
            {autocompleteValue?.unitSize && (<Grid item xs={12}>
              <Autocomplete
                options={unitSizes}
                onChange={(_event, value: string | null) => {
                  setOptionalValue(value ?? "");
                }}
                renderInput={(params: AutocompleteRenderInputParams) => <TextField
                  required={autocompleteValue.required}
                  label={autocompleteValue.unitSize}
                  variant="outlined" {...params} />}
              />
            </Grid>)}
            {autocompleteValue?.date && (<Grid item xs={12} sm={5}>
              <KeyboardDatePicker
                style={{ minWidth: "15em" }}
                autoComplete="off"
                autoOk
                variant="inline"
                inputVariant="outlined"
                label={autocompleteValue.date}
                format="MM/dd/yyyy"
                InputAdornmentProps={{ position: "end" }}
                value={optionalDate}
                disablePast
                onChange={setOptionalDate}
              />
            </Grid>)}
          </Grid>
        )}
        {newStatus && ![Action.Removed, Action.Skip].includes(newStatus as any) && (
          <>
            <ModalTitleText status={newStatus} />
            <Input
              name="reason"
              type="text"
              label="Reason"
              placeholder={displayPlaceholder(newStatus)}
              value={reason}
              onChange={(event) => onChange(event.target.value)}
              disabled={isSubmitting}
              required={true}
            />
          </>
        )}

        {newStatus === Action.Removed && (
          <StructuredReasons
            disabled={isSubmitting}
            label="Removal Reason*"
            onChange={handleRemovalReasonOnChange}
          />
        )}
      </DialogContent>
      {isSubmitting && newStatus === Action.Skip && bulkAction && (
        <Alert style={{ marginTop: 5 }} severity="info">
            Skipping applicants. This may take a while.
        </Alert>
      )}
      <DialogActions>
        <Button
          className={styles.confirmationButton}
          color="secondary"
          size="S"
          onClick={onCancel}
          disabled={isSubmitting}
        >
            Cancel
        </Button>
        <Button
          className={styles.confirmationButton}
          color="primary"
          size="S"
          disabled={isSubmitting || (
            newStatus === Action.Removed && !isValid) || (
            newStatus === Action.Skip && !isSkipValid)}
          onClick={onSubmitWrapper}
        >
          {isSubmitting ? "Submitting" : "Confirm"}
        </Button>
      </DialogActions>
    </Modal>
  );
};
export default ReasonModal;
